// Admin, users, backup, system, database settings API
import { api } from './client';
import type { CreateUserRequest, UpdateUserRequest } from './auth';

// Database monitoring interfaces
export interface DatabaseSizeInfo {
  total_size_bytes: number;
  total_size_mb: number;
  total_size_gb: number;
  table_sizes: TableSizeInfo[];
  index_sizes: IndexSizeInfo[];
  growth_rate?: DatabaseGrowthRate;
  storage_by_data_type: Record<string, StorageUsage>;
  last_calculated: string;
}

export interface TableSizeInfo {
  table_name: string;
  size_bytes: number;
  size_mb: number;
  row_count: number;
  index_size_bytes: number;
  index_size_mb: number;
  percentage: number;
}

export interface IndexSizeInfo {
  index_name: string;
  table_name: string;
  size_bytes: number;
  size_mb: number;
  is_unique: boolean;
  columns: string;
  percentage: number;
}

export interface DatabaseGrowthRate {
  daily_growth_mb: number;
  weekly_growth_mb: number;
  monthly_growth_mb: number;
  growth_trend: string;
}

export interface StorageUsage {
  size_bytes: number;
  size_mb: number;
  record_count: number;
  percentage: number;
}

export interface DatabaseHealthScore {
  overall_score: number;
  overall_grade: string;
  performance_score: number;
  storage_score: number;
  index_score: number;
  maintenance_score: number;
  recommendations: HealthRecommendation[];
  last_assessed: string;
}

export interface HealthRecommendation {
  category: string;
  priority: string;
  title: string;
  description: string;
  action: string;
  impact: string;
}

export interface MaintenanceTaskRequest {
  task_type: string;
  table_name?: string;
  sql?: string;
  dry_run: boolean;
}

export interface MaintenanceTaskResponse {
  task_type: string;
  dry_run: boolean;
  executed_at: string;
  message: string;
  sql?: string;
}

// Database Settings interfaces
export interface PostgresConfig {
  host: string;
  port: string;
  user: string;
  password: string;
  dbname: string;
  sslmode?: string;
}

export interface DatabaseInfo {
  type: string;
  connected: boolean;
  version?: string;
  database?: string;
  size?: string;
  path?: string;
  table_counts?: Record<string, number>;
}

export interface MigrationStatus {
  in_progress: boolean;
  current_table: string;
  total_tables: number;
  completed_tables: number;
  start_time: string;
  error?: string;
  records_copied: number;
}

// Admin API
export const adminAPI = {
  // System status
  getSystemStatus: () => {
    console.log('📊 Fetching system status');
    return api.get('/admin/status');
  },
  // System settings
  getSettings: () => {
    console.log('⚙️ Fetching system settings');
    return api.get('/admin/settings');
  },
  updateSettings: (data: Record<string, any>) => {
    console.log('⚙️ Updating system settings');
    return api.put('/admin/settings', data);
  },
  // Clients (for admin)
  getClients: () => {
    console.log('🏢 Fetching clients');
    return api.get('/clients');
  },
  // Weekly Digest Management
  getWeeklyDigestSchedule: () => {
    console.log('📅 Fetching weekly digest schedule');
    return api.get('/admin/weekly-digest/schedule');
  },
  previewWeeklyDigest: (clientId: number, projectId?: number, language?: string) => {
    console.log('👁️ Fetching weekly digest preview for client:', clientId, 'language:', language);
    const params = new URLSearchParams();
    if (projectId) params.append('project_id', projectId.toString());
    if (language) params.append('language', language);
    const queryString = params.toString();
    return api.get(`/admin/weekly-digest/preview/${clientId}${queryString ? '?' + queryString : ''}`);
  },
  sendWeeklyDigestNow: (data: { client_id: number; send_to_admin_only?: boolean; send_to_all?: boolean; recipients?: string[]; language?: string }) => {
    console.log('📧 Sending weekly digest:', data);
    return api.post('/admin/weekly-digest/send', data);
  },
  // Partner integration keys
  listPartnerKeys: (filters?: { client_id?: number; environment?: string; source_platform?: string }) => {
    console.log('🔑 Fetching partner keys');
    const params = new URLSearchParams();
    if (filters?.client_id) params.append('client_id', String(filters.client_id));
    if (filters?.environment) params.append('environment', filters.environment);
    if (filters?.source_platform) params.append('source_platform', filters.source_platform);
    const qs = params.toString();
    return api.get(`/admin/partner-keys${qs ? '?' + qs : ''}`);
  },
};

// Client API Keys API (supports both regular and partner keys)
export interface CreateApiKeyRequest {
  client_id: number;
  name: string;
  permissions?: string[];
  rate_limit_per_minute?: number;
  rate_limit_per_hour?: number;
  expires_at?: string | null;
  // Partner integration fields
  is_partner_key?: boolean;
  environment?: 'test' | 'prod';
  callback_url?: string;
  callback_key?: string;
  callback_secret?: string;
  source_platform?: string;
  source_tenant_id?: string;
}

export interface UpdateApiKeyRequest {
  name?: string;
  permissions?: string[];
  rate_limit_per_minute?: number;
  rate_limit_per_hour?: number;
  expires_at?: string | null;
  is_active?: boolean;
  // Partner rotation/update fields
  environment?: 'test' | 'prod';
  callback_url?: string;
  callback_key?: string;
  callback_secret?: string;
  source_platform?: string;
  source_tenant_id?: string;
}

export const clientApiKeysAPI = {
  list: (clientId: number) => {
    console.log('🔑 Fetching client API keys for client:', clientId);
    return api.get(`/clients/${clientId}/api-keys`);
  },
  get: (id: number) => api.get(`/api-keys/${id}`),
  create: (data: CreateApiKeyRequest) => {
    console.log('➕ Creating API key for client:', data.client_id);
    return api.post(`/clients/${data.client_id}/api-keys`, data);
  },
  update: (id: number, data: UpdateApiKeyRequest) => {
    console.log('📝 Updating API key:', id);
    return api.put(`/api-keys/${id}`, data);
  },
  revoke: (id: number, reason?: string) => {
    console.log('🚫 Revoking API key:', id);
    return api.post(`/api-keys/${id}/revoke`, { reason });
  },
  delete: (id: number) => {
    console.log('🗑️ Deleting API key:', id);
    return api.delete(`/api-keys/${id}`);
  },
};

// Superadmin Alert Center API
export const superadminAlertAPI = {
  getAlerts: (params?: { page?: number; page_size?: number; severity?: string; category?: string; event_type?: string; acknowledged?: string; from?: string; to?: string }) => {
    const qp = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== '') qp.append(k, String(v)); });
    }
    const qs = qp.toString();
    return api.get(`/admin/alert-center/alerts${qs ? '?' + qs : ''}`);
  },
  getAlert: (id: number) => api.get(`/admin/alert-center/alerts/${id}`),
  acknowledgeAlert: (id: number) => api.post(`/admin/alert-center/alerts/${id}/ack`),
  acknowledgeAll: (params?: { category?: string; severity?: string }) => {
    const qp = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => { if (v) qp.append(k, v); });
    }
    const qs = qp.toString();
    return api.post(`/admin/alert-center/alerts/ack-all${qs ? '?' + qs : ''}`);
  },
  getStats: () => api.get('/admin/alert-center/stats'),
  getConfigs: () => api.get('/admin/alert-center/configs'),
  updateConfig: (id: number, data: { enabled?: boolean; severity?: string; delivery_mode?: string; cooldown_minutes?: number }) =>
    api.put(`/admin/alert-center/configs/${id}`, data),
  resetConfigs: () => api.post('/admin/alert-center/configs/reset'),
  testPushover: () => api.post('/admin/alert-center/test-pushover'),
  testEmail: () => api.post('/admin/alert-center/test-email'),
  refreshPushover: () => api.post('/admin/alert-center/refresh-pushover'),
};

// Admin/Users API
export const usersAPI = {
  getAll: () => {
    console.log('👥 Fetching all users');
    return api.get('/admin/users');
  },
  create: (data: CreateUserRequest) => {
    console.log('➕ Creating new user:', data.username);
    return api.post('/admin/users', data);
  },
  update: (id: number, data: UpdateUserRequest) => {
    console.log('📝 Updating user:', id, data);
    return api.put(`/admin/users/${id}`, data);
  },
  delete: (id: number) => {
    console.log('🗑️ Deleting user:', id);
    return api.delete(`/admin/users/${id}`);
  },
};

// Product-aware default-credentials catalog (credentials + device signatures)
export const defaultCredsAPI = {
  stats: () => api.get('/admin/default-credentials/stats'),
  reload: () => api.post('/admin/default-credentials/reload'),
  list: (params: { kind?: string; key?: string; search?: string; page?: number; limit?: number }) =>
    api.get('/admin/default-credentials', { params }),
  create: (data: any) => api.post('/admin/default-credentials', data),
  update: (id: number, data: any) => api.put(`/admin/default-credentials/${id}`, data),
  remove: (id: number) => api.delete(`/admin/default-credentials/${id}`),
  listSignatures: (search?: string) => api.get('/admin/device-signatures', { params: { search } }),
  createSignature: (data: any) => api.post('/admin/device-signatures', data),
  updateSignature: (id: number, data: any) => api.put(`/admin/device-signatures/${id}`, data),
  removeSignature: (id: number) => api.delete(`/admin/device-signatures/${id}`),
};

// Admin/Backup API
export const backupAPI = {
  list: () => {
    console.log('📦 Fetching backups');
    return api.get('/admin/backups');
  },
  create: (type: 'database' | 'full' | 'app' | 'data' = 'full') => {
    console.log('📦 Creating backup:', type);
    return api.post(`/admin/backups?type=${type}`, null, { timeout: 600000 });
  },
  download: (filename: string) => {
    console.log('📥 Downloading backup:', filename);
    return api.get(`/admin/backups/${filename}`, { responseType: 'blob', timeout: 300000 });
  },
  delete: (filename: string) => {
    console.log('🗑️ Deleting backup:', filename);
    return api.delete(`/admin/backups/${filename}`);
  },
  restore: (filename: string) => {
    console.log('♻️ Restoring backup:', filename);
    return api.post(`/admin/backups/${filename}/restore`);
  },
  testRemoteConnection: () => {
    console.log('🔌 Testing remote backup connection');
    return api.post('/admin/backups/test-remote');
  },
  uploadToRemote: (filename: string) => {
    console.log('📤 Uploading backup to remote:', filename);
    return api.post(`/admin/backups/${filename}/upload-remote`, null, { timeout: 600000 });
  },
};

// Admin/Notification Logs API
export const notificationLogsAPI = {
  list: (params?: { page?: number; limit?: number; page_size?: number; status?: string; type?: string }) => {
    console.log('📬 Fetching notification logs');
    // Map 'limit' to 'page_size' for backend compatibility
    const apiParams: any = { ...params };
    if (params?.limit && !params?.page_size) {
      apiParams.page_size = params.limit;
      delete apiParams.limit;
    }
    return api.get('/notifications', { params: apiParams });
  },
  stats: () => {
    console.log('📊 Fetching notification stats');
    return api.get('/notifications/stats');
  },
  retry: (id: number) => {
    console.log('🔄 Retrying notification:', id);
    return api.post(`/notifications/${id}/retry`);
  },
  legacyQueue: (params?: { page?: number; page_size?: number; status?: string }) => {
    return api.get('/admin/notifications/legacy-queue', { params });
  },
  cancelLegacyQueue: () => {
    return api.post('/admin/notifications/legacy-queue/cancel');
  },
};

// Admin/Email Test API
export const emailAPI = {
  testEmail: (config: {
    smtp_host: string;
    smtp_port: number;
    smtp_user: string;
    smtp_password: string;
    smtp_from_email: string;
    smtp_from_name: string;
    smtp_use_tls: boolean;
  }) => {
    console.log('📧 Testing email configuration');
    return api.post('/admin/test-email', config);
  },
};

// Database Settings API
export const databaseSettingsAPI = {
  // Get current database info
  getInfo: () => {
    console.log('🗃️ Fetching database info');
    return api.get<{ success: boolean; data: DatabaseInfo }>('/admin/database/info');
  },
  // Test PostgreSQL connection
  testPostgres: (config: PostgresConfig) => {
    console.log('🔌 Testing PostgreSQL connection');
    return api.post<{ success: boolean; message?: string; error?: string }>('/admin/database/test-postgres', config);
  },
  // Start migration to PostgreSQL
  startMigration: (config: PostgresConfig) => {
    console.log('🚀 Starting database migration');
    return api.post<{ success: boolean; message?: string; error?: string }>('/admin/database/migrate', config);
  },
  // Get migration status
  getMigrationStatus: () => {
    console.log('📊 Fetching migration status');
    return api.get<{ success: boolean; data: MigrationStatus }>('/admin/database/migration-status');
  },
};
